import React from 'react';
import TableWindow from '../../../../components/Table/TableWindow';
import { GET_MODEL_IDS, GET_MODELS, ModelItem } from './Models.query';
import availableColumns from './Models.columns';
import useTablePages from '../../../../hooks/useTablePages';
import PageBar from '../../../../components/PageBar/PageBar';

import FilterBar, { defaultFilters, ModelFilters } from './FilterBarModel';
import { useOrderBy } from '../../../../hooks/useOrderBy';

import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import { Flex } from 'rebass';
import { PortalWithState } from 'react-portal';
import SimpleAddModal from '../../../../components/Modal/SimpleAddModal';
import { gql, useMutation } from '@apollo/client';
import useNotify from '../../../../hooks/useNotify';
import { getStringSearchOr } from '../../../../helpers/search';

const stringSearchFields = [
  {
    field: 'name',
    label: 'Name',
  },
];

const CREATE_MODEL = gql`
  mutation CreateModel($input: BackflowAssemblyModelInput!) {
    createBackflowAssemblyModel(input: $input) {
      id
      name
    }
  }
`;

type ModelsProps = {
  makeId?: string;
};

/**
 * @component Used for models page
 */
function Models({ makeId }: ModelsProps): JSX.Element {
  const convertStringSearch = (stringSearch?: string) => ({
    or: getStringSearchOr(stringSearchFields, stringSearch),
  });

  const convertFilters = (filters: ModelFilters) => {
    return {
      ...convertStringSearch(filters.modelName),
    };
  };

  const { showError, showSuccess } = useNotify();

  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<ModelFilters>(convertFilters, 'models', defaultFilters);

  const { orderBy, onHeaderClick } = useOrderBy(
    'models',
    availableColumns,
    'name'
  );

  const {
    loading,
    error,
    itemIds,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages<ModelItem>(
    'backflowAssemblyModels',
    GET_MODEL_IDS,
    {
      where: {
        ...queryFilters,
        backflowAssemblyMakeId: makeId,
      },
      limit: null,
      orderBy,
    },
    GET_MODELS,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    })
  );

  const [createModel] = useMutation<{
    createBackflowAssemblyModel: {
      id: string;
      name: string;
    };
  }>(CREATE_MODEL, {
    onCompleted: refetch,
    onError: (error) => {
      showError('Error creating model', error);
    },
  });

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'models');

  return (
    <Flex flexDirection="column" flexGrow={1}>
      <PageBar title="Models" />
      <PortalWithState closeOnEsc>
        {({ openPortal, closePortal, portal }) => (
          <>
            <FilterBar
              filters={storedFilters}
              setFilters={setStoredFilters}
              onAdd={makeId ? openPortal : undefined}
            />
            {portal(
              <SimpleAddModal
                title="Add Model"
                placeholder="model name"
                onClose={closePortal}
                onSave={async (value) => {
                  await createModel({
                    variables: {
                      input: {
                        backflowAssemblyMakeId: makeId,
                        name: value,
                      },
                    },
                  });
                  closePortal();
                }}
              />
            )}
          </>
        )}
      </PortalWithState>

      {loading && itemIds.length === 0 ? (
        'Loading...'
      ) : (
        <TableWindow
          columns={visibleColumns}
          itemIds={itemIds}
          itemLoader={itemLoader}
          onSelectionChange={setSelectedIds}
          selectedIds={selectedIds}
          onHeaderClick={onHeaderClick}
          orderBy={orderBy}
          columnConfiguration={columnConfiguration}
          setColumnConfiguration={setColumnConfiguration}
        />
      )}
    </Flex>
  );
}

export default Models;
